var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AmbientLight, AxesHelper, Clock, Mesh, MeshStandardMaterial, Object3D, Scene, WebGLRenderer, } from 'three';
import { DimensionsRenderer } from './DimensionsRenderer';
import { GlbConverter } from './modelPreviewService';
import { LayoutManagerV3 } from './layoutManager/LayoutManagerV3';
import { changeUserData, getUserData, hasUserData, setUserData } from './types';
export function createPartContainerName(partKey) {
    return `$$$<part-container>$$$-${partKey}`;
}
export class ConfiguratorView3D {
    constructor(canvas, sceneConfig, gltfExporter, usdzExporter, cameraManager, environment, modelLoader, materialsLibrary, renderer, plugin) {
        this.canvas = canvas;
        this.sceneConfig = sceneConfig;
        this.gltfExporter = gltfExporter;
        this.usdzExporter = usdzExporter;
        this.cameraManager = cameraManager;
        this.environment = environment;
        this.modelLoader = modelLoader;
        this.materialsLibrary = materialsLibrary;
        this.renderer = renderer;
        this.plugin = plugin;
        this.debug = false;
        this.disableRerender = false;
        this.showRulerFlag = false;
        this.clock = null;
        this.resizeHandler = () => {
            this.updateSizes();
            this.dimensionsRenderer.dimensionsRenderer2D.setSize(this.width, this.height);
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
            this.destroy();
        }
    }
    init(view3dConfiguration) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clock = new Clock();
            this.view3dConfiguration = view3dConfiguration;
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.cameraManager.init(view3dConfiguration, this.canvas);
            yield this.environment.init(this.renderer, this.scene);
            this.dimensionsRenderer = new DimensionsRenderer(this.width, this.height);
            this.initHandlers();
            this.startRendering();
            const getWidth = () => this.width;
            const getHeight = () => this.height;
            this.plugin.init({
                cameraManager: this.cameraManager,
                renderer: this.renderer,
                scene: this.scene,
                rootNode: this.rootNode,
                getWidth,
                getHeight,
            });
        });
    }
    showScene(sceneSpecification_1) {
        return __awaiter(this, arguments, void 0, function* (sceneSpecification, _keepCameraPosition = false) {
            yield this.updateMaterials(sceneSpecification.materials);
            const layout = new LayoutManagerV3(sceneSpecification).buildLayout();
            const nodesFactories = yield Promise.all(layout.containers.map((part) => {
                const partSpec = sceneSpecification.parts.find((partSpec) => partSpec.id === part.id);
                if (!partSpec) {
                    throw new Error(`Cannot find part with id "${part.id}"`);
                }
                return this.renderPart(part, partSpec);
            }));
            this.rootNode.children = [];
            for (const nodeFactory of nodesFactories) {
                this.rootNode.add(nodeFactory());
            }
            if (this.showRulerFlag) {
                this.showRuler();
            }
            this.plugin.onUpdateLayout();
        });
    }
    showSceneV2() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('Not supported');
        });
    }
    exportGltfScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.gltfExporter.export(this.createExportScene());
        });
    }
    exportUsdzScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.usdzExporter.export(this.createExportScene());
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('toggle-fullscreen', this.resizeHandler);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    startRendering() {
        requestAnimationFrame(() => {
            var _a, _b;
            this.cameraManager.update();
            const delta = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.getDelta()) !== null && _b !== void 0 ? _b : 0;
            this.plugin.update(delta);
            if (this.renderer instanceof WebGLRenderer) {
                this.renderer.clear();
                this.renderer.render(this.scene, this.cameraManager.camera);
                if (this.cameraManager && this.rootNode) {
                    this.dimensionsRenderer.render(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.camera, this.cameraManager.getControlsAzimuthalAngle(), this.showRulerFlag);
                }
                this.plugin.render();
                this.startRendering();
            }
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.add(this.sceneNode);
        this.dimensionScene2D = new Scene();
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        this.dimensionLinesNode = new Object3D();
        this.dimensionLinesNode.name = 'Ruler';
        this.scene.add(this.dimensionLinesNode);
        if (this.debug) {
            const axesHelper = new AxesHelper(1);
            this.scene.add(axesHelper);
        }
        this.applyConfig();
    }
    resize() {
        this.resizeHandler();
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    renderPart(containerPart, partSpecification) {
        return __awaiter(this, void 0, void 0, function* () {
            let model = null;
            if (partSpecification.fileName) {
                const template = yield this.modelLoader.load(partSpecification.fileName);
                model = template.clone();
                model.visible = partSpecification.visible;
                setUserData(model, { takePlace: false, webAr: { visible: true } });
                if (model.visible) {
                    changeUserData(model, { takePlace: partSpecification.takePlace });
                    for (const material of partSpecification.materials) {
                        const modelMesh = model.getObjectByName(material.mesh);
                        if (!(modelMesh instanceof Mesh)) {
                            console.warn(`Cannot find mesh "${material.mesh}" in model "${partSpecification.fileName}"`);
                            continue;
                        }
                        const modelMaterial = yield this.materialsLibrary.get(material.materialId);
                        if (!modelMaterial) {
                            console.warn(`Cannot find material "${material.materialId}" in library`);
                            continue;
                        }
                        if (modelMaterial instanceof MeshStandardMaterial && modelMaterial.envMapIntensity != 1) {
                            modelMaterial.envMap = this.scene.environment;
                        }
                        modelMesh.material = modelMaterial;
                    }
                    for (const { mesh, visible, webAr } of partSpecification.meshes) {
                        let found = false;
                        model.traverse((node) => {
                            if (node.name === mesh) {
                                found = true;
                                node.visible = visible;
                                changeUserData(node, { webAr });
                            }
                        });
                        if (!found) {
                            console.warn(`Cannot find mesh or group "${mesh}" in model "${partSpecification.fileName}"`);
                        }
                    }
                }
            }
            const outer = new Object3D();
            outer.applyMatrix4(containerPart.transform);
            return () => {
                outer.name = createPartContainerName(containerPart.id);
                if (model) {
                    outer.add(model);
                }
                return outer;
            };
        });
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    hideRuler() {
        this.showRulerFlag = false;
        this.dimensionsRenderer.clearScene(this.dimensionScene2D, this.dimensionLinesNode);
    }
    showRuler() {
        this.showRulerFlag = true;
        this.dimensionsRenderer.buildRuler(this.dimensionScene2D, this.dimensionLinesNode, this.rootNode, this.cameraManager.getControlsAzimuthalAngle());
    }
    createExportScene() {
        const sceneCopy = this.sceneNode.clone(true);
        const meshesToRemove = [];
        sceneCopy.traverse((node) => {
            const userData = getUserData(node);
            if (hasUserData(node) && !userData.webAr.visible) {
                meshesToRemove.push(node);
            }
            else {
                node.visible = true;
            }
        });
        for (const mesh of meshesToRemove) {
            mesh.removeFromParent();
        }
        return sceneCopy;
    }
    updateMaterials(materials) {
        return __awaiter(this, void 0, void 0, function* () {
            return Promise.all(materials.map((material) => this.materialsLibrary.updateMaterial(material)));
        });
    }
    createScreenshot(width, height) {
        return __awaiter(this, void 0, void 0, function* () {
            const pdfCameraPosition = this.view3dConfiguration.pdfCameraPosition;
            const glbConverter = new GlbConverter(width, height);
            const container = this.sceneNode.clone(true);
            return yield glbConverter.convertObject3DToBase64(container, this.view3dConfiguration.hdr, {
                cameraPosition: pdfCameraPosition,
            });
        });
    }
    applyConfig() {
        const sceneConfig = this.sceneConfig.scene;
        if (sceneConfig) {
            if (sceneConfig.environmentIntensity) {
                this.scene.environmentIntensity = sceneConfig.environmentIntensity;
            }
        }
    }
}
//# sourceMappingURL=ConfiguratorView3D.js.map